import React, { useEffect } from 'react';
import { DayPicker, DateFormatter } from 'react-day-picker';
import { ptBR } from 'date-fns/locale';
import { format, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-day-picker/dist/style.css';
import type { Locale } from 'date-fns';

interface CalendarProps {
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  availableDates?: Date[];
  minimumAdvanceDays?: number;
  maximumFutureDays?: number;
}

const Calendar: React.FC<CalendarProps> = ({ 
  selectedDate, 
  setSelectedDate,
  availableDates = [],
  minimumAdvanceDays = 1,
  maximumFutureDays = 30
}) => {
  // Função para formatar o cabeçalho do mês
  const formatCaption: DateFormatter = (month, options) => {
    return (
      <div className="flex justify-center items-center py-1">
        <span className="text-white font-medium text-sm sm:text-base">
          {format(month, 'MMMM yyyy', { locale: ptBR })}
        </span>
      </div>
    );
  };

  // Calculate min and max dates based on configuration
  const today = new Date();
  today.setHours(12, 0, 0, 0); // Definir meio-dia para evitar problemas de fuso horário
  
  const minDate = addDays(today, minimumAdvanceDays);
  const maxDate = addDays(today, maximumFutureDays);

  // Function to determine if a date is available
  const isDayAvailable = (date: Date) => {
    if (availableDates.length === 0) return true;
    
    // Normalizar a data para comparação (ignorando horas, minutos, segundos)
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    
    return availableDates.some(availableDate => {
      const normalizedAvailableDate = new Date(availableDate);
      normalizedAvailableDate.setHours(0, 0, 0, 0);
      
      return normalizedDate.getTime() === normalizedAvailableDate.getTime();
    });
  };

  // Handler para quando uma data é selecionada
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      // Normalizar a data selecionada para meio-dia para evitar problemas de fuso horário
      const normalizedDate = new Date(date);
      normalizedDate.setHours(12, 0, 0, 0);
      console.log('Calendar: Data selecionada (original):', date);
      console.log('Calendar: Data selecionada (normalizada):', normalizedDate);
      console.log('Calendar: Data formatada para API:', format(normalizedDate, 'yyyy-MM-dd'));
      setSelectedDate(normalizedDate);
    } else {
      console.log('Calendar: Seleção de data limpa');
      setSelectedDate(undefined);
    }
  };

  // Log para debug quando o componente renderiza ou as props mudam
  useEffect(() => {
    console.log('Calendar renderizado com:', {
      selectedDate: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : 'nenhum',
      availableDates: availableDates.length,
      minimumAdvanceDays,
      maximumFutureDays
    });
    
    if (availableDates.length > 0) {
      console.log('Primeiras 5 datas disponíveis:', 
        availableDates.slice(0, 5).map(d => format(d, 'yyyy-MM-dd'))
      );
    }
  }, [selectedDate, availableDates, minimumAdvanceDays, maximumFutureDays]);

  return (
    <div className="calendar-container">
      <style>{`
        .calendar-container .rdp {
          --rdp-cell-size: 32px;
          --rdp-accent-color: #1777CF;
          --rdp-background-color: rgba(23, 119, 207, 0.2);
          margin: 0;
          width: 100%;
        }
        @media (min-width: 640px) {
          .calendar-container .rdp {
            --rdp-cell-size: 36px;
          }
        }
        @media (min-width: 768px) {
          .calendar-container .rdp {
            --rdp-cell-size: 40px;
          }
        }
        .calendar-container .rdp-months {
          justify-content: center;
        }
        .calendar-container .rdp-month {
          width: 100%;
        }
        .calendar-container .rdp-table {
          width: 100%;
          max-width: 100%;
        }
        .calendar-container .rdp-caption {
          padding: 0;
          margin-bottom: 8px;
        }
        .calendar-container .rdp-head_cell {
          color: rgba(255, 255, 255, 0.6);
          font-weight: 500;
          font-size: 12px;
        }
        @media (min-width: 640px) {
          .calendar-container .rdp-head_cell {
            font-size: 14px;
          }
        }
        .calendar-container .rdp-day {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 400;
          border-radius: 8px;
          font-size: 12px;
        }
        @media (min-width: 640px) {
          .calendar-container .rdp-day {
            font-size: 14px;
          }
        }
        .calendar-container .rdp-day_selected {
          background-color: var(--rdp-accent-color);
          color: white;
          font-weight: 500;
        }
        .calendar-container .rdp-day_selected:hover {
          background-color: var(--rdp-accent-color);
          opacity: 0.8;
        }
        .calendar-container .rdp-day:hover:not(.rdp-day_selected) {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .calendar-container .rdp-day_outside {
          opacity: 0.3;
        }
        .calendar-container .rdp-day_disabled {
          opacity: 0.3;
        }
        .calendar-container .rdp-button:hover:not([disabled]) {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .calendar-container .rdp-nav_button {
          width: 24px;
          height: 24px;
          padding: 0;
          border-radius: 4px;
          color: rgba(255, 255, 255, 0.8);
        }
        @media (min-width: 640px) {
          .calendar-container .rdp-nav_button {
            width: 28px;
            height: 28px;
          }
        }
        .calendar-container .rdp-nav_button:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
      <DayPicker
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        locale={ptBR}
        weekStartsOn={0}
        formatters={{ formatCaption }}
        components={{
          IconLeft: () => <ChevronLeft size={16} className="sm:w-[18px] sm:h-[18px]" />,
          IconRight: () => <ChevronRight size={16} className="sm:w-[18px] sm:h-[18px]" />,
        }}
        modifiers={{
          today: new Date(),
          available: availableDates,
        }}
        modifiersStyles={{
          today: {
            fontWeight: 'bold',
            textDecoration: 'underline',
          },
          available: {
            fontWeight: 'bold',
          }
        }}
        disabled={[
          { before: minDate },
          { after: maxDate },
          (date) => !isDayAvailable(date)
        ]}
        fromMonth={minDate}
        toMonth={maxDate}
      />
    </div>
  );
};

export default Calendar;